import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Collapse } from '@mui/material';
import { GridPaginationModel, GridRowOrderChangeParams, GridRowParams } from '@mui/x-data-grid-pro';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import CDataGridPro from 'src/components/CDataGridPro';
import { CGridFooter } from 'src/components/custom-grid-data-components';
import { useDialogContext } from 'src/components/dialog/DialogContext';
import ServiceGuide, { ServicePreview } from 'src/components/guide/ServiceGuide';
import Iconify from 'src/components/iconify';
import { Page } from 'src/components/Page';
import useCallApiWithLoading from 'src/hooks/useCallApiWithLoading';
import useGetGridCol, { IMainVisualGridRow } from 'src/hooks/useGetGridCol';
import useGetGuideInfo from 'src/hooks/useGetGuideInfo';
import {
  ContentBox,
  ContentBoxButtons,
  ContentButtonAction,
  ContentButtonCreate,
  ContentButtonText,
} from 'src/layouts/form/FormButton';
import { INIT_PAGINATION, IPagination } from 'src/models/extensions/withPagination';
import { mainVisualApi } from 'src/service';
import { FORM_IDS, FormType } from 'src/static/constants';

import { MainVisualFormDialog } from './MainVisualFormDailog';

/**
 * ## MainVisual
 * 메인 화면 관리 페이지
 */

export const MainVisual = () => {
  const { getMainVisualGridCol } = useGetGridCol();
  const { callApiWithLoading } = useCallApiWithLoading();
  const { openDialog, closeDialog } = useDialogContext();
  const { getMainVisualAreas } = useGetGuideInfo();
  const { t } = useTranslation();
  // 그리드 row 데이터
  const [gridRows, setGridRows] = useState<IMainVisualGridRow[]>([]);
  // 페이지네이션
  const [pagination, setPagination] = useState<IPagination>(INIT_PAGINATION);
  // 순서 변경 모드
  const [isOrderMode, setIsOrderMode] = useState<boolean>(false);
  // 가이드 토글
  const [isGuideOpen, setIsGuideOpen] = useState(false);

  // 메인 비주얼 목록 조회
  const getMainVisualData = useCallback(async () => {
    const pageParams = {
      page: pagination.page,
      size: pagination.size,
    };
    await mainVisualApi.getMainvisual(pageParams).then((res) => {
      if (res) {
        const newGridRows = res.data.map((mainVisual, i) => {
          return { ...mainVisual, id: mainVisual.visualSid ?? i };
        });
        setGridRows(newGridRows);
        if (res.pagination) {
          setPagination(res.pagination);
        }
      }
    });
  }, []);

  useEffect(() => {
    getMainVisualData();
  }, [getMainVisualData]);

  // 등록 버튼: 등록 dialog open
  const handleAdd = () => {
    openDialog({
      id: FORM_IDS.MAINVISUAL,
      formType: FormType.CREATE,
      maxWidth: 'xl',
      dialogContent: (
        <MainVisualFormDialog formType={FormType.CREATE} onSubmitSuccess={handleSubmitSuccess} />
      ),
    });
  };

  // 그리드 더블 클릭 시: 수정 dialog open
  const handleRowDoubleClick = (param: GridRowParams<IMainVisualGridRow>) => {
    callApiWithLoading(async () => {
      await mainVisualApi.getDetail(param.row.visualSid).then((res) => {
        if (res) {
          openDialog({
            id: FORM_IDS.MAINVISUAL,
            formType: FormType.UPDATE,
            maxWidth: 'xl',
            dialogContent: (
              <MainVisualFormDialog
                formType={FormType.UPDATE}
                selectedData={res.data}
                onSubmitSuccess={handleSubmitSuccess}
              />
            ),
          });
        }
      });
    });
  };

  // 등록 & 수정 성공 시
  const handleSubmitSuccess = () => {
    closeDialog(FORM_IDS.MAINVISUAL);
    getMainVisualData();
  };

  // 페이지 모델 변경 시
  const handlePaginationModelChange = (paginationModel: GridPaginationModel) => {
    setPagination({
      ...pagination,
      page: paginationModel.page + 1,
      size: paginationModel.pageSize,
    });
    getMainVisualData();
  };

  // 순서 변경 후 데이터
  const handleRowOrderChange = async (param: GridRowOrderChangeParams) => {
    console.log(param);
  };

  const handleOrderMode = () => {
    setIsOrderMode(!isOrderMode);
    // 순서 변경하는 api 호출
  };

  // 가이드 토글
  const handleGuideToggle = () => {
    setIsGuideOpen(!isGuideOpen);
  };

  return (
    <Page>
      <ContentBox>
        <ContentBoxButtons>
          <ContentButtonAction onClick={handleOrderMode}>
            {isOrderMode ? t('common.button.orderChangeSave') : t('common.button.orderChange')}
          </ContentButtonAction>
          <ContentButtonCreate onClick={handleAdd}>
            {t('common.button.register')}
          </ContentButtonCreate>
          <ContentButtonText onClick={handleGuideToggle}>
            <Iconify icon="mdi:help-circle-outline" />
          </ContentButtonText>
        </ContentBoxButtons>
        <Collapse in={isGuideOpen}>
          <ServicePreview>
            <ServiceGuide areas={getMainVisualAreas()} />
          </ServicePreview>
        </Collapse>
        <CDataGridPro
          columns={getMainVisualGridCol()}
          rows={gridRows}
          rowCount={toJS(pagination.totalElements)}
          rowReordering={isOrderMode ? true : false}
          onRowOrderChange={handleRowOrderChange}
          rowHeight={150}
          paginationModel={{
            page: pagination.page - 1,
            pageSize: pagination.size,
          }}
          onPaginationModelChange={handlePaginationModelChange}
          onRowDoubleClick={handleRowDoubleClick}
          slots={{ footer: CGridFooter }}
        />
      </ContentBox>
    </Page>
  );
};

export default observer(MainVisual);
